import { useEffect, useRef, useState } from 'react'

interface TopicCardTooltipOptions {
  name: string
  description: string
  multiColumnDescription: boolean
  hasLongDescription: boolean
}

const isClipped = (element: HTMLElement | null) =>
  !!element &&
  (element.scrollHeight > element.clientHeight + 1 ||
    element.scrollWidth > element.clientWidth + 1)

export const useTopicCardTooltip = ({
  name,
  description,
  multiColumnDescription,
  hasLongDescription,
}: TopicCardTooltipOptions) => {
  const [truncated, setTruncated] = useState({
    title: false,
    description: false,
  })
  const titleTextRef = useRef<HTMLSpanElement>(null)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const descriptionPreviewRef = useRef<HTMLDivElement>(null)

  const tooltipText = truncated.title
    ? truncated.description
      ? `${name}\n—\n${description}`
      : name
    : truncated.description
    ? description
    : undefined

  useEffect(() => {
    let isActive = true
    const measure = () => {
      if (!isActive) return

      const previewVisible =
        !!descriptionPreviewRef.current &&
        getComputedStyle(descriptionPreviewRef.current).display !== 'none'
      const title = isClipped(titleTextRef.current)
      const description = previewVisible
        ? hasLongDescription
        : isClipped(descriptionRef.current)

      setTruncated((previous) =>
        previous.title === title && previous.description === description
          ? previous
          : { title, description }
      )
    }

    measure()
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    ;[
      titleTextRef.current,
      descriptionRef.current,
      descriptionPreviewRef.current,
    ].forEach((element) => {
      if (element) observer?.observe(element)
    })

    if (!observer) window.addEventListener('resize', measure)
    document.fonts?.ready.then(measure)

    return () => {
      isActive = false
      observer?.disconnect()
      if (!observer) window.removeEventListener('resize', measure)
    }
  }, [name, description, multiColumnDescription, hasLongDescription])

  return { tooltipText, titleTextRef, descriptionRef, descriptionPreviewRef }
}
